// Story 11.1 — Mutation hook para `PUT /bots/:id/files`. Maneja toast y
// limpia el dirty flag tras save exitoso.
// Story 11.5 — wirea al `validation-store` para que el indicador del toolbar
// refleje el resultado de cada save.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { filesApi } from '@/lib/api-client';
import type { SaveFileResult } from '@/lib/editor-types';
import { queryKeys } from '@/lib/query-keys';
import { useEditorStore } from '@/stores/editor-store';
import { useValidationStore } from '@/stores/validation-store';

// P-336 (review 11.1): removido `force?: boolean` del shape del frontend.
// El backend lo soporta para casos de admin recovery, pero exponerlo desde
// la UI permitía a cualquier caller (DevTools / browser console) bypassear
// la validación silenciosamente. MVP: la única vía de saltarse el guard es
// llamar al endpoint backend directo con auth admin.
interface SaveInput {
  botId: number;
  path: string;
  content: string;
}

export function useSaveFile() {
  const qc = useQueryClient();
  const markSaved = useEditorStore((s) => s.markSaved);
  const startValidation = useValidationStore((s) => s.start);
  const setValidationResult = useValidationStore((s) => s.setResult);

  return useMutation<SaveFileResult, Error, SaveInput>({
    mutationFn: ({ botId, path, content }) => filesApi.save(botId, { path, content }),
    onMutate: () => {
      startValidation();
    },
    onSuccess: (result, vars) => {
      // Story 11.5 — proyecta resultado al validation-store (errors +
      // warnings de la última validación). El indicador del toolbar y el
      // ProblemsPanel leen de acá.
      setValidationResult(result.validation.errors, result.validation.warnings);
      if (result.saved) {
        markSaved(vars.path);
        // Refrescamos el árbol por si el save creó un nuevo flow/test.
        void qc.invalidateQueries({ queryKey: queryKeys.files(vars.botId) });
        if (result.validation.ok) {
          toast.success(`✓ Guardado ${vars.path}`);
        } else {
          toast.warning(
            `Guardado con ${result.validation.errors.length} advertencia(s) — ver Problemas`,
          );
        }
      } else {
        toast.error(
          `No se pudo guardar: ${result.validation.errors.length} error(es) de validación`,
        );
      }
    },
    onError: (err) => {
      // Si el request fall por red, marcamos invalid con un solo error
      // sintético — el indicador no debe quedarse en `validating` para
      // siempre.
      setValidationResult([{ code: 'NETWORK_ERROR', message: err.message }], []);
      toast.error(`Error de red al guardar: ${err.message}`);
    },
  });
}
